import { gql, useMutation, useQuery } from '@apollo/client'
import numeral from 'numeral'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import {
  ApplyPreSaleLotteryMutation,
  ApplyPreSaleLotteryMutationVariables,
  LotteryConfirmationQuery,
  LotteryConfirmationQueryVariables,
  PaymentDetailFragmentDoc,
  PaymentMethod,
  PreSaleLotteryConfirmationFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useRouter } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { getPrefrenceObject, removePrefernce } from '@/core/native/storage'
import { useRootAuth } from '@/core/rootAuth'

import { ApplyPreSaleLotteryValue } from '../LotteryDetail/template/LotteryDetailForm'
import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { analytics } = useAnalytics()
  const { router, routerPages } = useRouter<{
    preSaleId: string
  }>()
  const [choice, setChoice] = useState<ApplyPreSaleLotteryValue>()
  const rootAuthInfo = useRootAuth()
  /** 外部アプリのルート認証に失敗したかどうかの状態 */
  const [isRootAuthFailed, setIsRootAuthFailed] = useState<boolean>(false)
  const { t } = useTranslation('lotteryConfirmation')
  const { showError } = useMessage()

  const { data, loading } = useQuery<
    LotteryConfirmationQuery,
    LotteryConfirmationQueryVariables
  >(QUERY, {
    variables: {
      id: router.query.preSaleId,
      clientId: rootAuthInfo.clientId,
      token: rootAuthInfo.token,
    },
    onError: error => {
      setIsRootAuthFailed(
        error?.graphQLErrors?.some(
          error => error?.extensions?.code === 'FORBIDDEN'
        ) ?? false
      )
    },
  })

  const [applyPreSaleLottery, { loading: submitting }] = useMutation<
    ApplyPreSaleLotteryMutation,
    ApplyPreSaleLotteryMutationVariables
  >(APPLY_PRE_SALE_LOTTERY)

  useEffect(() => {
    ;(async () => {
      const v = await getPrefrenceObject<ApplyPreSaleLotteryValue>(
        'preSaleLotteryApplicationInput'
      )

      // localStorageにデータがない場合、応募ページを表示
      if (v.preSaleId !== router.query.preSaleId) {
        router.replace(
          generatePath(routerPages.LotteryDetail, {
            preSaleId: router.query.preSaleId,
          })
        )
      }

      setChoice(v)
    })()
  }, [getPrefrenceObject])

  const isKonbini = useMemo(
    () => choice?.paymentMethod === PaymentMethod.Konbini,
    [choice?.paymentMethod]
  )
  const billingStatements = useMemo(() => {
    // 代金や手数料など、支払のすべてをブレイクダウンした配列
    return choice?.choices?.map(({ itemId, skuId }, index) => {
      const item = data?.preSale?.items?.find(i => i?.id === itemId)
      const itemPrice = skuId
        ? item?.skuSettings?.units?.find(s => s?.id === skuId)?.price ??
          item?.price
        : item?.price
      const purchaseCount = choice?.purchaseCountChoices?.[index]?.count ?? 0

      // 代金や手数料など、支払のすべてをブレイクダウンした配列
      const statements = [
        ...[
          {
            label: t('totalPrice'),
            value: `￥${numeral((itemPrice ?? 0) * purchaseCount).format(
              '0,0'
            )}`,
          },
          ...(item?.fees?.collections || []).map(fee => {
            const feeValue = fee?.fee ?? 0
            return {
              label: fee?.feeName,
              value:
                fee?.per === 'order'
                  ? `￥${numeral(feeValue).format('0,0')}`
                  : `￥${numeral(feeValue * purchaseCount).format('0,0')}`,
            }
          }),
        ],
        ...(isKonbini
          ? [
              {
                label: item?.fees?.konbini?.feeName,
                value: `￥${numeral(item?.fees?.konbini?.fee).format('0,0')}`,
              },
            ]
          : []),
      ]

      const totalFee = item?.fees?.collections?.reduce(
        (acc, fee) =>
          acc +
          (fee?.per === 'order'
            ? fee?.fee ?? 0
            : (fee?.fee ?? 0) * purchaseCount),
        0
      )

      return {
        statements,
        totalPrice:
          (itemPrice ?? 0) * purchaseCount +
          (totalFee ?? 0) +
          (isKonbini ? item?.fees?.konbini?.fee ?? 0 : 0),
      }
    })
  }, [choice, data?.preSale?.items, t])

  return {
    state: {
      isRootAuthFailed,
      preSale: data?.preSale,
      loading,
      submitting,
      paymentSource: data?.paymentSource,
      choice,
      billingStatements,
      paymentMethod: choice?.paymentMethod ?? PaymentMethod.CreditCard,
    },
    actions: {
      async onSubmit() {
        const choices = choice?.choices?.map(
          ({ itemId, skuId }) => skuId ?? itemId
        )
        const purchaseCountChoices = choice?.purchaseCountChoices?.map(
          purchaseCount => purchaseCount.count
        )
        if (!choices || !purchaseCountChoices) return

        await analytics?.track('click_preSaleLotteryApply', {
          applicationKey: config.applicationKey,
          preSaleId: router.query.preSaleId,
          choiceCount: choice?.choices.length,
          paymentMethod:
            choice?.paymentMethod === 'konbini'
              ? 'convenience_store'
              : 'credit_card',
        })

        const paymentMethod = choice?.paymentMethod ?? PaymentMethod.CreditCard
        await applyPreSaleLottery({
          variables: {
            inputs: {
              preSaleId: router.query.preSaleId,
              choices,
              purchaseCountChoices,
              paymentMethod,
              fullName:
                paymentMethod === PaymentMethod.Konbini
                  ? choice?.fullName
                  : undefined,
            },
            /**
             * https://studio.apollographql.com/graph/utoniq/variant/development/schema/reference/objects/Mutation#applyPreSaleLottery
             * ルート認証情報の抽選である場合は、以下の引数も付与する
             */
            ...(router.query.preSaleId === rootAuthInfo.preSaleId
              ? {
                  clientId: rootAuthInfo.clientId,
                  token: rootAuthInfo.token,
                }
              : {}),
          },
        })
          .then(async applicants => {
            // LocalStorageに保存した一次データを削除
            await removePrefernce('preSaleLotteryApplicationInput')
            router.replace(
              generatePath(routerPages.LotteryCompletion, {
                preSaleId: router.query.preSaleId,
              })
            )
          })
          .catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query LotteryConfirmation($id: ID!, $clientId: String, $token: String) {
    preSale(id: $id, clientId: $clientId, token: $token) {
      ...PreSaleLotteryConfirmation
    }
    paymentSource {
      ...PaymentDetail
    }
  }
  ${PreSaleLotteryConfirmationFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const APPLY_PRE_SALE_LOTTERY = gql`
  mutation ApplyPreSaleLottery(
    $inputs: ApplyPreSaleLotteryInput!
    $clientId: String
    $token: String
  ) {
    applyPreSaleLottery(inputs: $inputs, clientId: $clientId, token: $token) {
      id
    }
  }
`
